/**
 * ReplyDraftControl — the run-level reply-draft opt-out (AC-5, design AD-3).
 *
 * ONE per launch, above the policy group, reachable without expanding anything.
 * It is mounted by `LaunchForm` from `binding.roles.replyDraft` rather than
 * passed in by the host, which is what makes it impossible for a host to ship
 * the form without the control.
 *
 * Default ON, so an AM who ignores it gets today's behaviour exactly.
 *
 * The control does NOT own the write into the payload: `buildLaunchPayload`
 * writes the declared key on EVERY build, `true` or `false`, so an untouched
 * form still carries it explicitly. Both sides read the same rule
 * (`replyDraftValue`), so "what the switch shows" and "what the payload
 * carries" cannot drift — an omitted key is the failure mode AC-5 exists for,
 * and a `false` that silently becomes "draft the reply" is the same defect
 * wearing a different hat.
 */

import ToggleSwitch from "~/common/components/ToggleSwitch";

/** Stated beside the switch: "off" must plainly mean no reply is drafted. */
export const REPLY_DRAFT_OFF_CONSEQUENCE =
  "Skipped — no reply draft, no pending-send, no eDoc wait.";

export const REPLY_DRAFT_ON_CONSEQUENCE =
  "ora drafts a reply and waits for you to send it.";

export interface ReplyDraftControlProps {
  /** The DECLARED run-level key (`binding.roles.replyDraft`) — used only to
   *  scope the element id, never to spell a payload key here. */
  fieldKey: string;
  /** The declared title, so a contract-side rewording reaches the AM. */
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}

export default function ReplyDraftControl({
  fieldKey,
  label,
  checked,
  disabled = false,
  onChange,
}: ReplyDraftControlProps) {
  return (
    <div className="flex touch-target items-center gap-2" data-testid="launch-reply-draft">
      {/* The shared slider, not a bare checkbox. This control already declared
          role="switch" — it was a SWITCH wearing a checkbox’s clothes, and the
          clothes were whatever blue the operating system felt like. */}
      <ToggleSwitch
        id={`launch-reply-draft-${fieldKey}`}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        label={label}
        data-testid="launch-reply-draft-input"
      />
      <label
        className="text-xs font-medium text-primary-gray-light-700"
        htmlFor={`launch-reply-draft-${fieldKey}`}
      >
        {label}
      </label>
    </div>
  );
}
